import { CheckCircleIcon, ExclamationTriangleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import { ProgressBar, Spinner } from '@/shared/ui/atoms';

import type { UploadProgressItem } from '../../../hooks/useTutorDocumentUpload';

export interface UploadProgressListProps {
  /** Todos os arquivos do lote atual, cada um com sua situação. */
  readonly items: readonly UploadProgressItem[];
}

/**
 * Indicador de situação de uma linha do lote.
 *
 * Extraído da lista porque a apresentação por status é uma cadeia de seis ramos (upload em
 * andamento, indexando, indexado, duas falhas e a fila) — isolar essa decisão deixa
 * `UploadProgressList` livre para só cuidar da estrutura da lista.
 */
function UploadItemIndicator({ item }: { readonly item: UploadProgressItem }) {
  const { t } = useTranslation('tutorAI');

  if (item.status === 'uploading') {
    return <ProgressBar progressPercentage={item.percent} shouldShowPercentage />;
  }

  if (item.status === 'importing') {
    return (
      <div className="flex items-center gap-nano">
        {/* `aria-hidden` no spinner: a `<ul>` da lista é `aria-live="polite"`, e o
            `role="status"` do `Spinner` (que embute `aria-live="polite"` por conta própria)
            aninhado dentro dela é o anti-padrão que a WAI-ARIA desaconselha — regiões vivas
            aninhadas têm anúncio duplicado em algumas combinações de leitor de tela/navegador,
            porque as duas regiões reagem à mesma inserção de nó. Escondendo o spinner da
            árvore de acessibilidade, sobra um único caminho de anúncio: o texto visível ao
            lado, coberto pela `<ul>`. */}
        <span aria-hidden="true">
          <Spinner size="sm" />
        </span>
        <span className="text-ctx-content-placeholder ts-caption-xxs">
          {t('classDetail.upload.indexing')}
        </span>
      </div>
    );
  }

  if (item.status === 'done') {
    return (
      <div className="flex items-center gap-nano text-base-success-600">
        <CheckCircleIcon className="h-mini w-mini" />
        <span className="ts-caption-xxs">{t('classDetail.upload.status.done')}</span>
      </div>
    );
  }

  if (item.status === 'uploadFailed') {
    return (
      <div className="flex items-center gap-nano text-base-danger-600">
        <XCircleIcon className="h-mini w-mini" />
        <span className="ts-caption-xxs">{t('classDetail.upload.status.uploadFailed')}</span>
      </div>
    );
  }

  if (item.status === 'importFailed') {
    return (
      <div className="flex items-center gap-nano text-base-warning-600">
        <ExclamationTriangleIcon className="h-mini w-mini" />
        <span className="ts-caption-xxs">{t('classDetail.upload.status.importFailed')}</span>
      </div>
    );
  }

  return (
    <span className="text-ctx-content-placeholder ts-caption-xxs">
      {t('classDetail.upload.status.queued')}
    </span>
  );
}

/**
 * Feedback visual do lote de upload: uma linha por arquivo, com a situação de cada um.
 *
 * As situações têm indicadores diferentes porque a informação disponível é diferente. Em
 * `uploading` o percentual vem de bytes reais transferidos (`onUploadProgress` do Axios) e
 * aparece numa `ProgressBar`. Em `importing` não existe progresso a reportar —
 * `POST /documents/{id}/import` é uma chamada única no servidor — e a linha mostra um
 * `Spinner` indeterminado. As demais são estados terminais ou de espera, com rótulo textual.
 *
 * Renderiza `null` sem lote em andamento, para não deixar um contêiner vazio no layout.
 *
 * A lista é `aria-live="polite"` porque cinco das seis situações (todas menos `importing`)
 * eram silenciosas para leitor de tela — só o `role="status"` do `Spinner` anunciava algo.
 * Sem isso, um item concluir ou falhar não gerava nenhum aviso, e os toasts de fim de lote só
 * mitigam parcialmente porque agora é o lote inteiro, item a item, que importa reportar.
 *
 * @example
 * ```tsx
 * <UploadProgressList items={items} />
 * ```
 */
export function UploadProgressList({ items }: UploadProgressListProps) {
  if (items.length === 0) return null;

  return (
    <ul aria-live="polite" className="flex flex-col gap-xxs">
      {items.map(item => (
        <li
          key={item.id}
          className="flex flex-col gap-quark rounded-sm border-hairline border-ctx-layout-border p-xxs"
        >
          <span className="truncate text-ctx-content-title ts-paragraph-xs">{item.name}</span>
          <UploadItemIndicator item={item} />
        </li>
      ))}
    </ul>
  );
}
